const usd = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mi = (n) => (n == null ? "—" : (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 }));
const LEG_LABEL = { home_out: "Home → first job", between: "Job → job", home_back: "Last job → Home" };

// The first `freeMiles` of each day's driving (route order, may span legs) is unpaid — mirrors the backend
export const withDayDeduction = (legs, freeMiles) => {
  const left = {};
  return legs.map((g) => {
    const miles = g.miles == null ? 0 : Number(g.miles) || 0;
    const rem = left[g.date] ?? freeMiles;
    const unpaid = Math.min(miles, rem);
    left[g.date] = rem - unpaid;
    return { ...g, unpaid_miles: unpaid, paid_miles: miles - unpaid };
  });
};

// Green = paid miles on this leg, red = none paid; job → job stays plain when it is paid in full
const rowTone = (g) => {
  if (g.kind === "between" && !g.unpaid_miles) return "bg-white";
  return g.paid_miles > 0 ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-900";
};

const Td = ({ children, className = "", ...rest }) => <td className={`border border-zinc-400 px-2 py-1.5 ${className}`} {...rest}>{children}</td>;

// Read-only mileage legs in the Daily Report table style with a per-day subtotal
export const MileageLegs = ({ legs: rawLegs, minLeg, perMile, showPay = false, testId = "cost-mileage" }) => {
  const legs = withDayDeduction(rawLegs, minLeg);
  const cols = ["Date", "Miles", "Paid mi", "Leg", "From", "To", ...(showPay ? ["Mileage pay"] : [])];
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <p className="text-[11px] text-zinc-700 bg-zinc-50 border border-zinc-300 rounded px-2 py-1.5" data-testid={`${testId}-rule`}>
        <b>{minLeg}-mile rule:</b> the first <b>{minLeg} mi of each day's driving</b> is unpaid — counted from Home → first job onward and carried across legs until used up.
        Every mile after that is paid (<span className="text-emerald-700 font-semibold">green = paid miles</span>, <span className="text-red-700 font-semibold">red = no miles paid</span>).
        Miles are rounded to the nearest whole mile (0.5 rounds up) and come from the Travel Map plan, or from your Home address and the job addresses.
      </p>
      <div className="overflow-x-auto border border-zinc-400 rounded">
        <table className="w-full border-collapse text-xs" data-testid={`${testId}-table`}>
          <thead>
            <tr className="bg-black text-white">
              {cols.map((h) => <th key={h} className="border border-zinc-700 px-2 py-2 font-bold text-[12px] text-center">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {legs.length === 0 && <tr><td colSpan={cols.length} className="text-center text-zinc-500 py-3">No travel legs for the selected days.</td></tr>}
            {legs.map((g, i) => {
              const dayEnd = legs[i + 1]?.date !== g.date;
              const dayMiles = legs.filter((x) => x.date === g.date).reduce((a, x) => a + x.paid_miles, 0);
              return [
                <tr key={g.key || i} className={rowTone(g)} data-testid={`${testId}-leg-${i}`} data-kind={g.kind} data-paid={g.paid_miles > 0 ? "yes" : "no"}>
                  <Td className="text-center whitespace-nowrap">{g.date}</Td>
                  <Td className="text-right tabular-nums font-semibold" data-testid={`${testId}-leg-miles-${i}`} title={g.source || "no distance available"}>{mi(g.miles)}</Td>
                  <Td className="text-right tabular-nums" data-testid={`${testId}-leg-paid-${i}`}>{g.miles == null ? "—" : mi(g.paid_miles)}</Td>
                  <Td className="text-center whitespace-nowrap font-medium">{LEG_LABEL[g.kind]}</Td>
                  <Td>{g.from}</Td>
                  <Td>{g.to}</Td>
                  {showPay && <Td className="text-right tabular-nums" data-testid={`${testId}-leg-pay-${i}`}>{usd(g.paid_miles * perMile)}</Td>}
                </tr>,
                dayEnd && (
                  <tr key={`${g.date}-total`} className="bg-zinc-200 font-semibold" data-testid={`${testId}-day-total-${g.date}`}>
                    <Td className="text-center whitespace-nowrap">{g.date}</Td>
                    <Td />
                    <Td className="text-right tabular-nums" data-testid={`${testId}-day-miles-${g.date}`}>{mi(dayMiles)}</Td>
                    <Td className="text-left" colSpan={3}>Day total — paid miles (first {minLeg} mi unpaid)</Td>
                    {showPay ? <Td className="text-right tabular-nums" data-testid={`${testId}-day-pay-${g.date}`}>{usd(dayMiles * perMile)}</Td> : null}
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
